import { Link, router } from '@inertiajs/react';
import { formatDistanceToNow } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MessageSquare,
  UserRound,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getCountryDisplay } from '@/lib/countries';
import type {
  Pagination,
  ReviewComment,
  SubmissionReviewRow,
  SubmissionStatus,
} from '@/types';

interface SubmissionReviewTableProps {
  eventSlug: string;
  submissions: SubmissionReviewRow[];
  pagination: Pagination;
  totalParticipants: number;
}

export function SubmissionReviewTable({
  eventSlug,
  submissions,
  pagination,
  totalParticipants,
}: SubmissionReviewTableProps) {
  const basePath = `/admin/${eventSlug}/review`;

  const getReturnUrl = () => {
    const params = window.location.search.replace(/^\?/, '');
    return params ? `${basePath}?${params}` : basePath;
  };

  const changePage = (newPage: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', newPage.toString());
    router.get(`${basePath}?${params.toString()}`);
  };

  const toggleAiRankSort = () => {
    const params = new URLSearchParams(window.location.search);
    const currentSortBy = params.get('sortBy') ?? 'submittedAt';
    const currentSortOrder = params.get('sortOrder') ?? 'desc';
    if (currentSortBy === 'aiRank') {
      params.set('sortOrder', currentSortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      params.set('sortBy', 'aiRank');
      params.set('sortOrder', 'desc');
    }
    params.delete('page');
    router.get(`${basePath}?${params.toString()}`);
  };

  const currentQuery = new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.search,
  );

  const statusColors: Record<SubmissionStatus, string> = {
    draft: 'bg-slate-100 text-slate-700 border-slate-300',
    received: 'bg-blue-100 text-blue-800 border-blue-300',
    priority_waiting: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    asking_self_finance_trip: 'bg-purple-100 text-purple-800 border-purple-300',
    approved: 'bg-green-100 text-green-800 border-green-300',
    onboarding_request: 'bg-cyan-100 text-cyan-800 border-cyan-300',
    onboarding_expired: 'bg-gray-100 text-gray-800 border-gray-300',
    onboarding_complete: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    rejected: 'bg-red-100 text-red-800 border-red-300',
    waiting_list: 'bg-amber-100 text-amber-800 border-amber-300',
    withdrawn: 'bg-orange-100 text-orange-800 border-orange-300',
    archived: 'bg-slate-100 text-slate-800 border-slate-300',
  };

  const statusLabels: Record<SubmissionStatus, string> = {
    draft: 'Draft',
    received: 'Received',
    priority_waiting: 'Priority Waiting',
    asking_self_finance_trip: 'Asking Self Finance',
    approved: 'Approved',
    onboarding_request: 'Onboarding Request',
    onboarding_expired: 'Onboarding Expired',
    onboarding_complete: 'Onboarding Complete',
    rejected: 'Rejected',
    waiting_list: 'Waiting List',
    withdrawn: 'Withdrawn',
    archived: 'Archived',
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getGitHubAvatarUrl = (githubUsername: string | null) => {
    if (!githubUsername) return null;
    const username = githubUsername.replace(
      /^https?:\/\/(www\.)?github\.com\//,
      '',
    );
    return `https://github.com/${username}.png`;
  };

  const formatPercentile = (pct: number | null | undefined) => {
    if (pct == null) return null;
    return `P${Math.round(pct * 100)}`;
  };

  const percentileColor = (pct: number | null | undefined) => {
    if (pct == null) return 'border-muted bg-muted text-muted-foreground';
    if (pct >= 0.9) return 'border-emerald-500 bg-emerald-100 text-emerald-900';
    if (pct >= 0.75) return 'border-emerald-300 bg-emerald-50 text-emerald-800';
    if (pct >= 0.5) return 'border-amber-300 bg-amber-50 text-amber-800';
    if (pct >= 0.25) return 'border-orange-300 bg-orange-50 text-orange-800';
    return 'border-red-300 bg-red-50 text-red-800';
  };

  const teamBestPercentile = (
    members: Array<{
      llmEvaluation: { pctComposite: number | null } | null;
    }>,
  ) => {
    const values = members
      .map((m) => m.llmEvaluation?.pctComposite ?? null)
      .filter((v): v is number => typeof v === 'number');
    if (values.length === 0) return null;
    return Math.max(...values);
  };

  const showAiRank = submissions.some(
    (s) => teamBestPercentile(s.members) != null,
  );

  // The members' ages, youngest first; people without one are skipped.
  const memberAges = (row: SubmissionReviewRow) =>
    row.members
      .map((member) => ({
        name: member.fullName,
        age: member.profile?.age ?? null,
      }))
      .filter((m): m is { name: string; age: number } => m.age != null)
      .sort((a, b) => a.age - b.age);

  const medianAge = (ages: number[]) => {
    if (ages.length === 0) return null;
    const mid = Math.floor(ages.length / 2);
    const median =
      ages.length % 2 === 1 ? ages[mid] : (ages[mid - 1] + ages[mid]) / 2;
    return Number.isInteger(median) ? median : median.toFixed(1);
  };

  const showReviewer = submissions.some((s) => s.assignedReviewer != null);

  const commentCount = (row: SubmissionReviewRow) =>
    row.comments.length +
    row.members.reduce((sum, member) => sum + member.comments.length, 0);

  const renderComment = (comment: ReviewComment) => (
    <div key={comment.id} className="rounded bg-muted/60 px-2 py-1">
      <p className="whitespace-pre-wrap">{comment.body}</p>
      <p className="mt-0.5 text-[10px] text-muted-foreground">
        {comment.authorName} ·{' '}
        {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
      </p>
    </div>
  );

  if (submissions.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No submissions found matching your filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Members</TableHead>
              {showAiRank && (
                <TableHead>
                  <button
                  type="button"
                  onClick={toggleAiRankSort}
                  className="inline-flex items-center gap-1 hover:underline"
                >
                    AI Rank
                    {currentQuery.get('sortBy') === 'aiRank' && (
                      <span className="text-xs">
                        {currentQuery.get('sortOrder') === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                </TableHead>
              )}
              <TableHead>Comments</TableHead>
              <TableHead>Age</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Modality</TableHead>
              <TableHead>Cohort</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Status</TableHead>
              {showReviewer && <TableHead>Reviewer</TableHead>}
              <TableHead>Submitted</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {submissions.map((submissionData) => (
              <TableRow key={submissionData.submission.id}>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {[...submissionData.members]
                      .sort((a, b) => a.fullName.localeCompare(b.fullName))
                      .map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-2 rounded-md bg-muted px-2 py-1"
                        >
                          <Avatar className="h-6 w-6">
                            <AvatarImage
                              src={
                                getGitHubAvatarUrl(member.github) || undefined
                              }
                              alt={member.fullName}
                            />
                            <AvatarFallback className="text-xs">
                              {getInitials(member.fullName)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{member.fullName}</span>
                          {member.github && (
                            <a
                              href={
                                member.github.startsWith('http')
                                  ? member.github
                                  : `https://github.com/${member.github}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-foreground"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      ))}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {[...submissionData.members]
                      .sort((a, b) => a.fullName.localeCompare(b.fullName))
                      .map((member) => {
                        const pct = member.llmEvaluation?.pctComposite ?? null;
                        return (
                          <span
                            key={`pct-${member.id}`}
                            title={
                              pct == null
                                ? `${member.fullName}: no AI eval`
                                : `${member.fullName}: ${formatPercentile(pct)}`
                            }
                            className={`inline-flex h-5 min-w-[2.25rem] items-center justify-center rounded-md border px-1.5 font-mono text-[10px] ${percentileColor(pct)}`}
                          >
                            {formatPercentile(pct) ?? '—'}
                          </span>
                        );
                      })}
                  </div>
                </TableCell>
                {showAiRank && (
                  <TableCell>
                    {(() => {
                      const best = teamBestPercentile(submissionData.members);
                      return (
                        <Badge
                          variant="outline"
                          className={`font-mono ${percentileColor(best)}`}
                        >
                          {formatPercentile(best) ?? 'N/A'}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                )}
                <TableCell>
                  {(() => {
                    const count = commentCount(submissionData);
                    if (count === 0) {
                      return <span className="text-muted-foreground">—</span>;
                    }
                    const memberComments = submissionData.members.filter(
                      (member) => member.comments.length > 0,
                    );
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-default items-center gap-1 text-sm">
                            <MessageSquare className="h-4 w-4" />
                            {count}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="max-w-sm space-y-3 border bg-popover p-3 text-popover-foreground"
                        >
                          {submissionData.comments.length > 0 && (
                            <div className="space-y-1">
                              <div className="font-semibold">Application</div>
                              {submissionData.comments.map(renderComment)}
                            </div>
                          )}
                          {memberComments.map((member) => (
                            <div key={member.id} className="space-y-1">
                              <div className="font-semibold">
                                {member.fullName}
                              </div>
                              {member.comments.map(renderComment)}
                            </div>
                          ))}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const ages = memberAges(submissionData);
                    const median = medianAge(ages.map((m) => m.age));
                    if (median == null) {
                      return (
                        <span className="text-muted-foreground text-sm">
                          N/A
                        </span>
                      );
                    }
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default text-sm">
                            {median}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          {ages.map((m) => (
                            <div key={m.name}>
                              {m.name}: {m.age}
                            </div>
                          ))}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {submissionData.submission.country ? (
                    <Badge variant="outline">
                      {getCountryDisplay(submissionData.submission.country)}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">N/A</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      submissionData.submission.modality === 'solo'
                        ? 'border-purple-300 bg-purple-100 text-purple-800'
                        : submissionData.submission.modality === 'team'
                          ? 'border-green-300 bg-green-100 text-green-800'
                          : 'border-amber-300 bg-amber-100 text-amber-800'
                    }
                  >
                    {submissionData.submission.modality === 'solo'
                      ? 'Solo'
                      : submissionData.submission.modality === 'team'
                        ? 'Team'
                        : 'Team Looking'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      submissionData.submission.cohort === 'priority'
                        ? 'border-orange-300 bg-orange-100 text-orange-800'
                        : 'border-blue-300 bg-blue-100 text-blue-800'
                    }
                  >
                    {submissionData.submission.cohort === 'priority'
                      ? 'Priority'
                      : 'Final'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {submissionData.members.some(
                      (member) => member.gender === 'female',
                    ) && (
                      <Badge
                        variant="outline"
                        className="border-pink-300 bg-pink-100 text-pink-800"
                      >
                        <UserRound className="mr-1 h-3 w-3" />
                        Woman
                      </Badge>
                    )}
                    {submissionData.hasFlightRequest && (
                      <Badge
                        variant="outline"
                        className="border-blue-300 bg-blue-100 text-blue-800"
                      >
                        ✈️ Asking Flight
                      </Badge>
                    )}
                    {submissionData.reviews.map((review) => (
                      <Badge
                        key={review.id}
                        variant="outline"
                        className={
                          review.qualification === 'hell_yes'
                            ? 'border-green-500 bg-green-100 text-green-800'
                            : review.qualification === 'yes'
                              ? 'border-green-300 bg-green-50 text-green-700'
                              : review.qualification === 'maybe'
                                ? 'border-yellow-300 bg-yellow-50 text-yellow-700'
                                : review.qualification === 'no'
                                  ? 'border-red-300 bg-red-50 text-red-700'
                                  : 'border-red-500 bg-red-100 text-red-800'
                        }
                      >
                        {review.qualification === 'hell_yes'
                          ? '🔥 Hell Yes'
                          : review.qualification === 'yes'
                            ? '👍 Yes'
                            : review.qualification === 'maybe'
                              ? '🤔 Maybe'
                              : review.qualification === 'no'
                                ? '👎 No'
                                : '🚫 Hell No'}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={statusColors[submissionData.submission.status]}
                  >
                    {statusLabels[submissionData.submission.status]}
                  </Badge>
                </TableCell>
                {showReviewer && (
                  <TableCell>
                    {submissionData.assignedReviewer ? (
                      <span className="text-sm">
                        {submissionData.assignedReviewer.fullName}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">
                        Unassigned
                      </span>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {submissionData.submission.submittedAt ? (
                    <div className="space-y-1">
                      <div className="text-sm">
                        {new Date(
                          submissionData.submission.submittedAt,
                        ).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          timeZone: 'America/Santiago',
                        })}{' '}
                        {new Date(
                          submissionData.submission.submittedAt,
                        ).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                          timeZone: 'America/Santiago',
                        })}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {formatDistanceToNow(
                          new Date(submissionData.submission.submittedAt),
                          {
                            addSuffix: true,
                          },
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Not submitted
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      href={`/admin/${eventSlug}/submissions/${
                        submissionData.submission.id
                      }?returnTo=${encodeURIComponent(getReturnUrl())}`}
                    >
                      View Details
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between px-4 py-4">
        <div className="text-muted-foreground text-sm">
          Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} submissions (
          {submissions.reduce((sum, s) => sum + s.members.length, 0)} of{' '}
          {totalParticipants} participants)
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((pageNum) => {
                if (pagination.totalPages <= 7) return true;
                if (pageNum === 1 || pageNum === pagination.totalPages)
                  return true;
                if (
                  pageNum >= pagination.page - 1 &&
                  pageNum <= pagination.page + 1
                )
                  return true;
                return false;
              })
              .map((pageNum, index, array) => {
                const showEllipsis =
                  index > 0 && pageNum - array[index - 1] > 1;

                return (
                  <div key={pageNum} className="flex items-center gap-1">
                    {showEllipsis && (
                      <span className="px-2 text-muted-foreground">...</span>
                    )}
                    <Button
                      variant={
                        pageNum === pagination.page ? 'default' : 'outline'
                      }
                      size="sm"
                      onClick={() => changePage(pageNum)}
                      className="min-w-[2.5rem]"
                    >
                      {pageNum}
                    </Button>
                  </div>
                );
              })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
